import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import {
	EventEntityType,
	type FormField,
	FormFieldType,
	type RequestState,
	type WorkflowConfig,
	isTerminalRequestState,
} from '@kfolks/types'
import { isAdmin } from '@/auth'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AuditTimeline } from '@/components/AuditTimeline'
import { RequestWorkflowTimeline } from '@/components/RequestWorkflowTimeline'
import { TicketsSection } from '@/components/TicketsSection'
import { useAuth } from '@/hooks/useAuth'
import { CommentsPanel } from '@/modules/comments'
import {
	type ApprovalDTO,
	type RequestDetailDTO,
	type RequestInfoGateDTO,
	type RequestStateHistoryDTO,
	requestCenterApi,
} from '@/services/request-center-api'
import { Input } from '@/components/ui/input'
import type { InfoGateField } from '@kfolks/types'
import { requestCommentApi } from '@/services/request-comment-api'
import { apiClient } from '@/services/api.client'
import { ReplyRequiredChip } from './_infoRequestNotice'
import { RequestStateBadge } from './_stateBadge'

/**
 * Read-only request detail (Spec 1). Renders the request's submitted values
 * labelled by the immutable formSchema snapshot, so the page reflects the
 * exact form the user filled — even if an admin has since edited the
 * request type.
 */
export function RequestDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data, isLoading, error } = useQuery({
		queryKey: ['request', id],
		queryFn: () => requestCenterApi.getRequest(id ?? ''),
		enabled: Boolean(id),
	})

	// The `/requests/types` backend only exposes a by-slug fetch — the
	// request DTO carries `requestTypeId` (UUID) but not the slug, so we
	// list the catalogue once (cached 5 min) and resolve the type client-
	// side. Catalog is small (~10 rows) and TanStack shares the cache
	// across the whole app.
	const { data: allTypes } = useQuery({
		queryKey: ['request-types', { includeInactive: true }],
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true }),
		staleTime: 5 * 60 * 1000,
	})
	const requestType = allTypes?.find((t) => t.id === data?.requestTypeId) ?? null

	if (!id) return <ErrorState message="Missing request id." />
	if (isLoading) return <DetailSkeleton />
	if (error) {
		const status = isAxiosError(error) ? error.response?.status : undefined
		const msg = status === 404 ? 'Request not found, or you do not have access to it.' : 'Failed to load the request.'
		return <ErrorState message={msg} />
	}
	if (!data) return <ErrorState message="Request not available." />

	const workflowConfig = (requestType?.workflowConfig ?? null) as WorkflowConfig | null
	return <DetailView data={data} workflowConfig={workflowConfig} />
}

function DetailView({ data, workflowConfig }: { data: RequestDetailDTO; workflowConfig: WorkflowConfig | null }) {
	// Approval timeline for this request — the merged workflow timeline needs
	// the persisted rows to render per-step status (approved/pending/…).
	const { data: timeline } = useQuery({
		queryKey: ['approvals-timeline', data.id],
		queryFn: () => requestCenterApi.listApprovalsByRequest(data.id),
	})
	// Pending info-gates: backend returns rows ONLY for the submitter / admin.
	// `isFinal` short-circuits the query — terminal-state requests never have
	// open gates, so we skip the round-trip.
	const isFinal = isTerminalRequestState(data.currentState as RequestState)
	const { data: pendingGates } = useQuery({
		queryKey: ['pending-info-gates', data.id],
		queryFn: () => requestCenterApi.listPendingInfoGates(data.id),
		enabled: !isFinal,
	})
	const hasPendingGate = (pendingGates ?? []).length > 0
	return (
		<div className="mx-auto max-w-2xl space-y-6">
			<DetailHeader data={data} hasPendingGate={hasPendingGate} />
			{(pendingGates ?? []).map((gate) => (
				<InfoGateAnswerForm key={gate.id} requestId={data.id} gate={gate} />
			))}
			<PendingApprovalBanner requestId={data.id} currentState={data.currentState as RequestState} />
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Workflow</CardTitle>
				</CardHeader>
				<CardContent>
					<RequestWorkflowTimeline
						workflowConfig={workflowConfig}
						persistedRows={timeline?.data ?? []}
						currentApprovalId={null}
					/>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Submitted values</CardTitle>
				</CardHeader>
				<CardContent>
					<FormDataView data={data} />
				</CardContent>
			</Card>
			<CommentsPanel
				entityType={EventEntityType.REQUEST_CENTER_REQUEST}
				entityId={data.id}
				apiClient={requestCommentApi}
			/>
			<TicketsSection requestId={data.id} requestState={data.currentState as RequestState} />
			<AuditTimeline requestId={data.id} />
		</div>
	)
}

/**
 * **DEPRECATED — Spec 3 rollback lever (task P14.6).**
 *
 * Append-only state-history audit trail for the request. Replaced in
 * `DetailView` by `<AuditTimeline>`, which reads the unified `events`
 * table and covers comments / file attachments / ticket lifecycle /
 * priority transforms in addition to state transitions.
 *
 * Per design D9, this component stays in the codebase (not rendered)
 * for one archive cycle so we can re-mount it as a one-line fallback
 * if `<AuditTimeline>` regresses in production. Underscore-prefixed
 * to satisfy `unused-imports/no-unused-vars` until it's removed.
 *
 * **Remove on the archive cycle AFTER Spec 3 ships.**
 */
/* v8 ignore start — rollback-lever, intentionally unrendered until removed (task P14.6) */
export function _AuditHistoryCard({ requestId }: { requestId: string }) {
	// Collapsed by default — the audit trail is secondary context. The row
	// count sits in the header so the viewer has signal without expanding.
	const [open, setOpen] = useState(false)
	const { data, isLoading, error } = useQuery({
		queryKey: ['request-state-history', requestId],
		queryFn: () => requestCenterApi.listRequestStateHistory(requestId),
	})
	const rows: RequestStateHistoryDTO[] = data?.data ?? []
	const contentId = `audit-history-${requestId}`
	return (
		<Card>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				aria-expanded={open}
				aria-controls={contentId}
				className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			>
				<div className="flex items-center gap-2">
					<CardTitle className="text-base">Audit history</CardTitle>
					{rows.length > 0 ? (
						<span className="text-xs text-muted-foreground">
							· {rows.length} transition{rows.length === 1 ? '' : 's'}
						</span>
					) : null}
				</div>
				<span aria-hidden className="text-lg leading-none text-muted-foreground">
					{open ? '▴' : '▾'}
				</span>
			</button>
			{open ? (
				<CardContent id={contentId}>
					{isLoading ? (
						<p className="text-sm text-muted-foreground">Loading…</p>
					) : error ? (
						<p className="text-sm text-muted-foreground">No audit history available.</p>
					) : rows.length === 0 ? (
						<p className="text-sm text-muted-foreground">No transitions recorded yet.</p>
					) : (
						<ol className="space-y-3" data-testid="request-audit-history">
							{rows.map((entry) => (
								<AuditRow key={entry.id} entry={entry} />
							))}
						</ol>
					)}
				</CardContent>
			) : null}
		</Card>
	)
}

function AuditRow({ entry }: { entry: RequestStateHistoryDTO }) {
	const when = new Date(entry.createdAt)
	const whenStr = Number.isNaN(when.getTime()) ? entry.createdAt : when.toLocaleString()
	const transition = entry.fromState ? `${entry.fromState} → ${entry.toState}` : entry.toState
	return (
		<li className="border-l-2 border-muted pl-3">
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span className="font-medium">{formatAction(entry.triggerAction)}</span>
				<Badge variant="outline">{transition}</Badge>
				<span className="text-xs text-muted-foreground">{whenStr}</span>
			</div>
			<p className="text-xs text-muted-foreground">
				by <UserName userId={entry.triggeredBy} />
			</p>
			{entry.comment ? <p className="mt-1 text-xs italic text-muted-foreground">“{entry.comment}”</p> : null}
		</li>
	)
}

/**
 * Humanise the `WorkflowAction` verb the engine persists (SUBMIT, APPROVE,
 * REJECT, CANCEL, PROVISION, COMPLETE) into a headline for the row. Falls
 * back to the raw verb so novel actions still render readably.
 */
function formatAction(action: string): string {
	const label =
		(
			{
				SUBMIT: 'Submitted',
				APPROVE: 'Approved',
				REJECT: 'Rejected',
				CANCEL: 'Cancelled',
				PROVISION: 'Provisioned',
				COMPLETE: 'Completed',
				INFO_GATE_ANSWER: 'Info-gate answered',
			} as Record<string, string>
		)[action.toUpperCase()] ?? action
	return label
}
/* v8 ignore stop */

/**
 * "You have a pending approval on this request — approve now" banner.
 *
 * Fetches the full approval timeline for the request and filters to pending
 * rows the *viewer* is assigned to (`viewerMatch` is `direct` / `manager` /
 * `group`). The backend already scopes the timeline to the caller, so we
 * only need to pick the ones still pending. Silent when:
 *   - the request is in a terminal state
 *   - there are no pending approvals assigned to the viewer
 *   - the timeline hasn't loaded yet (keep the page quiet, don't flash)
 */
function PendingApprovalBanner({ requestId, currentState }: { requestId: string; currentState: RequestState }) {
	const isTerminal = isTerminalRequestState(currentState)

	const { data: timeline } = useQuery({
		queryKey: ['approvals-timeline', requestId],
		queryFn: () => requestCenterApi.listApprovalsByRequest(requestId),
		enabled: !isTerminal,
	})

	if (isTerminal) return null
	const rows = timeline?.data ?? []
	const mine = rows.filter((r): r is ApprovalDTO => r.status === 'pending' && r.viewerMatch !== null)
	if (mine.length === 0) return null

	return (
		<Card className="border-blue-300 bg-blue-50">
			<CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
				<p className="text-sm text-blue-900">
					{mine.length === 1
						? 'You have a pending approval on this request.'
						: `You have ${mine.length} pending approvals on this request.`}
				</p>
				<div className="flex flex-wrap gap-2">
					{mine.map((a) => (
						<Button key={a.id} size="sm" asChild>
							<Link to={`/requests-center/approvals/${a.id}`}>
								Approve step {a.stepOrder} — {a.stepLabel ?? a.stepName}
							</Link>
						</Button>
					))}
				</div>
			</CardContent>
		</Card>
	)
}

function DetailHeader({ data, hasPendingGate }: { data: RequestDetailDTO; hasPendingGate: boolean }) {
	return (
		<header className="space-y-3">
			<div className="flex flex-wrap items-center gap-3">
				<h1 className="text-2xl font-semibold tracking-tight">{data.requestNumber}</h1>
				<RequestStateBadge state={data.currentState} />
				<Badge variant="outline">{capitalize(data.priority)} priority</Badge>
				{hasPendingGate ? <ReplyRequiredChip dataTestId="request-detail-reply-required-chip" /> : null}
				<div className="ml-auto">
					<CancelButton request={data} />
				</div>
			</div>
			<dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
				<DescTerm label="Submitted" value={formatDate(data.submittedAt)} />
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Submitter</dt>
					<dd className="mt-1 text-sm">
						<UserName userId={data.submitterId} />
					</dd>
				</div>
			</dl>
		</header>
	)
}

/**
 * Cancel button — visible only when the viewer is the submitter OR an
 * admin, AND the request is in a non-final state. Optimistic-free: the
 * mutation invalidates the request query and the server response replaces
 * the view.
 */
function CancelButton({ request }: { request: RequestDetailDTO }) {
	const { user } = useAuth()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [confirming, setConfirming] = useState(false)
	const mutation = useMutation({
		mutationFn: (reason?: string) => requestCenterApi.cancelRequest(request.id, reason),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['request', request.id] })
			void navigate('/requests-center/my')
		},
	})

	const admin = isAdmin(user?.role)
	const isSubmitter = user?.userId === request.submitterId
	// Use the shared terminal-state helper so the cancel button stays in
	// sync with the engine's notion of terminal (APPROVED is non-terminal
	// in Spec 3 — the request is still cancellable until ticket lifecycle
	// flips it to COMPLETED).
	const nonFinal = !isTerminalRequestState(request.currentState as RequestState)
	if (!nonFinal || (!isSubmitter && !admin)) return null

	if (!confirming) {
		return (
			<Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
				Cancel request
			</Button>
		)
	}
	return (
		<div className="flex items-center gap-2">
			<span className="text-xs text-muted-foreground">Are you sure?</span>
			<Button variant="destructive" size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate(undefined)}>
				{mutation.isPending ? 'Cancelling…' : 'Yes, cancel'}
			</Button>
			<Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
				Keep open
			</Button>
		</div>
	)
}

/**
 * Submitter-only form rendered when the backend returns a pending
 * `request_info_gates` row for this request. Two flavours:
 *
 *  - **Side-loop** (`gate.parentApprovalId !== null`): an approver clicked
 *    "Request more info" while reviewing. Renders the approver's note (if
 *    present), the dynamic field set, and a submit button. Answer-on-success
 *    resumes the parent approval's SLA — the workflow does NOT advance.
 *  - **Step-style** (`gate.parentApprovalId === null`, legacy): the gate is
 *    a workflow step. Same UI, but answer-on-success advances the workflow
 *    past the gate. Kept for in-flight requests parked on a step-style gate.
 *
 * Backend already enforces submitter-only (returns `[]` for everyone else)
 * so this component renders only when the API response is non-empty.
 */
function InfoGateAnswerForm({ requestId, gate }: { requestId: string; gate: RequestInfoGateDTO }) {
	const queryClient = useQueryClient()
	const fields = (gate.fieldsSnapshot ?? []) as InfoGateField[]
	const [values, setValues] = useState<Record<string, unknown>>(() => initialFormValues(fields))
	const [error, setError] = useState<string | null>(null)

	const mutation = useMutation({
		mutationFn: () => requestCenterApi.answerInfoGate(requestId, gate.stepId, values),
		onSuccess: async () => {
			setError(null)
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['request', requestId] }),
				queryClient.invalidateQueries({ queryKey: ['pending-info-gates', requestId] }),
				queryClient.invalidateQueries({ queryKey: ['approvals-timeline', requestId] }),
			])
		},
		onError: (e) => {
			const status = isAxiosError(e) ? e.response?.status : undefined
			setError(
				status === 422
					? 'Some required fields are missing — please fill them in.'
					: 'Could not submit your answer. Try again.',
			)
		},
	})

	const onSubmit = (event: FormEvent) => {
		event.preventDefault()
		// Defence-in-depth: the backend re-validates required fields, but we
		// short-circuit here so a click without entries doesn't burn a request.
		const missing = fields.filter((f) => f.required && (values[f.id] === undefined || values[f.id] === ''))
		if (missing.length > 0) {
			setError(`Please fill in: ${missing.map((m) => m.label).join(', ')}`)
			return
		}
		mutation.mutate()
	}

	const isSideLoop = gate.parentApprovalId !== null
	return (
		<Card className="border-amber-300 bg-amber-50">
			<CardHeader>
				<CardTitle className="text-base text-amber-900">
					{isSideLoop ? 'An approver needs more information' : 'Additional information required'}
				</CardTitle>
			</CardHeader>
			<CardContent>
				{gate.requestedByApproverNote ? (
					<p className="mb-4 rounded border border-amber-200 bg-white p-3 text-sm text-amber-900">
						<span className="font-medium">Approver's note:</span> {gate.requestedByApproverNote}
					</p>
				) : null}
				<form onSubmit={onSubmit} className="space-y-4" data-testid="info-gate-answer-form">
					{fields.map((field) => (
						<InfoGateFieldInput
							key={field.id}
							field={field}
							value={values[field.id]}
							onChange={(v) => setValues((prev) => ({ ...prev, [field.id]: v }))}
						/>
					))}
					{error ? <p className="text-sm text-red-600">{error}</p> : null}
					<Button type="submit" disabled={mutation.isPending}>
						{mutation.isPending ? 'Submitting…' : 'Submit answer'}
					</Button>
				</form>
			</CardContent>
		</Card>
	)
}

function initialFormValues(fields: InfoGateField[]): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	for (const f of fields) {
		out[f.id] = f.type === 'checkbox' ? false : ''
	}
	return out
}

function InfoGateFieldInput({
	field,
	value,
	onChange,
}: {
	field: InfoGateField
	value: unknown
	onChange: (next: unknown) => void
}) {
	const id = `info-gate-field-${field.id}`
	const label = (
		<label htmlFor={id} className="block text-sm font-medium text-gray-900">
			{field.label}
			{field.required ? <span className="ml-1 text-red-600">*</span> : null}
		</label>
	)

	if (field.type === 'textarea') {
		return (
			<div>
				{label}
				<textarea
					id={id}
					value={typeof value === 'string' ? value : ''}
					onChange={(e) => onChange(e.target.value)}
					required={field.required}
					rows={4}
					className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
				/>
			</div>
		)
	}
	if (field.type === 'select') {
		return (
			<div>
				{label}
				<select
					id={id}
					value={typeof value === 'string' ? value : ''}
					onChange={(e) => onChange(e.target.value)}
					required={field.required}
					className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
				>
					<option value="">Select…</option>
					{(field.options ?? []).map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			</div>
		)
	}
	if (field.type === 'checkbox') {
		return (
			<div className="flex items-center gap-2">
				<input
					id={id}
					type="checkbox"
					checked={Boolean(value)}
					onChange={(e) => onChange(e.target.checked)}
					className="h-4 w-4 rounded border-input"
				/>
				<label htmlFor={id} className="text-sm font-medium text-gray-900">
					{field.label}
					{field.required ? <span className="ml-1 text-red-600">*</span> : null}
				</label>
			</div>
		)
	}
	// text / number / date — handled by Input.
	return (
		<div>
			{label}
			<Input
				id={id}
				type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
				value={typeof value === 'string' || typeof value === 'number' ? value : ''}
				onChange={(e) =>
					onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)
				}
				required={field.required}
				className="mt-1"
			/>
		</div>
	)
}

function FormDataView({ data }: { data: RequestDetailDTO }) {
	const fields = data.requestTypeSnapshot?.fields ?? []
	if (fields.length === 0) return <p className="text-sm text-muted-foreground">No fields.</p>
	return (
		<dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
			{fields.map((f) => (
				<FormDataRow key={f.id} field={f} value={data.formData[f.id]} />
			))}
		</dl>
	)
}

function FormDataRow({ field, value }: { field: FormField; value: unknown }) {
	return (
		<div>
			<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{field.label}</dt>
			<dd className="mt-1 break-words text-sm">
				<FormDataValue field={field} value={value} />
			</dd>
		</div>
	)
}

const AUTOCOMPLETE_FAMILY = new Set<FormFieldType>([
	FormFieldType.AUTOCOMPLETE,
	FormFieldType.AUTOCOMPLETE_MULTI,
	FormFieldType.USER_PICKER,
	FormFieldType.USER_PICKER_MULTI,
	FormFieldType.PROJECT_PICKER,
	FormFieldType.COUNTRY_PICKER,
])

function FormDataValue({ field, value }: { field: FormField; value: unknown }) {
	if (value === undefined || value === null || value === '') return <>—</>
	if (Array.isArray(value) && value.length === 0) return <>—</>

	// Autocomplete-family fields store IDs but should display labels. Resolve
	// via the same data source the renderer used to populate the picker.
	if (AUTOCOMPLETE_FAMILY.has(field.type) && field.dataSource) {
		const ids = Array.isArray(value) ? value.map(String) : [String(value)]
		return <DataSourceLabels dataSourceKey={field.dataSource} ids={ids} />
	}

	// Composite repeater — one row per provider/access bundle.
	if (field.type === FormFieldType.PROVIDER_ACCESS_LIST && Array.isArray(value)) {
		return <ProviderAccessEntriesView entries={value as ProviderAccessEntryValue[]} />
	}

	// Static option lists (SELECT / MULTISELECT / RADIO / PRIORITY) — values are
	// codes; show the option label instead of the raw code.
	if (Array.isArray(value)) {
		return <>{value.map((v) => resolveOptionLabel(field, v)).join(', ')}</>
	}

	if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>

	// FILE / FILE_MULTI are file-service UUIDs — lookup is out of scope until
	// the file widget is wired. Show the id as a hint so audits aren't blank.
	if (field.type === FormFieldType.FILE || field.type === FormFieldType.FILE_MULTI) {
		return <span className="font-mono text-xs text-muted-foreground">{String(value)}</span>
	}

	return <>{resolveOptionLabel(field, value)}</>
}

interface ProviderAccessEntryValue {
	provider?: unknown
	accessLevel?: unknown
	durationDays?: unknown
}

function ProviderAccessEntriesView({ entries }: { entries: ProviderAccessEntryValue[] }) {
	const providerIds = entries.map((e) => String(e.provider ?? '')).filter(Boolean)
	const { data: items } = useQuery({
		queryKey: ['data-source-search', 'providers.all', { all: true }],
		queryFn: () => requestCenterApi.searchDataSource('providers.all', { limit: 100 }),
		staleTime: 5 * 60 * 1000,
		enabled: providerIds.length > 0,
	})
	const nameById = new Map((items?.data ?? []).map((it) => [it.value, it.name ?? it.label]))

	if (entries.length === 0) return <>—</>

	return (
		<ul className="space-y-0.5">
			{entries.map((entry, idx) => {
				const provider = String(entry.provider ?? '')
				const level = String(entry.accessLevel ?? '')
				const duration = typeof entry.durationDays === 'number' ? entry.durationDays : null
				return (
					<li key={`${provider}:${level}:${duration ?? 'x'}:${idx}`}>
						{nameById.get(provider) ?? provider}
						<span className="mx-1.5 text-muted-foreground">·</span>
						<span className="uppercase tracking-wide text-muted-foreground text-xs">{level}</span>
						{duration !== null ? (
							<>
								<span className="mx-1.5 text-muted-foreground">·</span>
								<span className="text-xs text-muted-foreground">{duration} days</span>
							</>
						) : null}
					</li>
				)
			})}
		</ul>
	)
}

function resolveOptionLabel(field: FormField, raw: unknown): string {
	const value = String(raw)
	if (!field.options || field.options.length === 0) return value
	const match = field.options.find((o) => o.value === value)
	return match ? match.label : value
}

/**
 * Resolve a JWT user id (`users.userId`) to a display name via
 * `/users/{id}/display`. Any-authenticated-viewer endpoint (valid JWT
 * required, no role gate) — distinct from the admin-only `GET /users/{id}`
 * which returns the full row.
 *
 * Different from `DataSourceLabels` which targets the auto-complete data
 * sources (employees, projects, etc.) — those index by `employeeId`, not
 * by `userId`.
 */
function UserName({ userId }: { userId: string }) {
	const { data, isLoading } = useQuery({
		queryKey: ['user-display', userId],
		queryFn: async () => {
			const res = await apiClient.get<{ displayName?: string; email?: string; userId: string } | null>(
				`/users/${userId}/display`,
			)
			return res.data
		},
		staleTime: 5 * 60 * 1000,
	})
	if (isLoading) return <span className="text-muted-foreground">…</span>
	if (!data) return <>{userId}</>
	const name = data.displayName || data.email || userId
	const showEmail = data.email && data.displayName && data.email !== data.displayName
	return (
		<span>
			{name}
			{showEmail ? <span className="ml-1 text-muted-foreground">({data.email})</span> : null}
		</span>
	)
}

/**
 * Resolve one or more data-source IDs to their display labels.
 *
 * We hit the same `/data-sources/:key/search` endpoint the picker uses (no `q`),
 * cache via TanStack Query, and look the IDs up in the result list. If an ID
 * isn't in the first page, we fall back to showing the raw value so the row is
 * still readable. Single shared TanStack cache means multiple rows backed by
 * the same data source share one HTTP call.
 */
function DataSourceLabels({ dataSourceKey, ids }: { dataSourceKey: string; ids: string[] }) {
	const { data: items, isLoading } = useQuery({
		queryKey: ['data-source-search', dataSourceKey, { all: true }],
		queryFn: () => requestCenterApi.searchDataSource(dataSourceKey, { limit: 100 }),
		staleTime: 5 * 60 * 1000,
	})

	if (isLoading) return <span className="text-muted-foreground">…</span>
	const lookup = new Map((items?.data ?? []).map((it) => [it.value, it.label]))
	const labels = ids.map((id) => lookup.get(id) ?? id)
	return <>{labels.join(', ')}</>
}

function DescTerm({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
			<dd className="mt-1 text-sm">{value}</dd>
		</div>
	)
}

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-2xl space-y-3">
			<div className="h-7 w-1/2 animate-pulse rounded bg-muted" />
			<div className="h-48 animate-pulse rounded-lg border bg-muted/40" />
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}

function formatDate(iso: string): string {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleString()
}

function capitalize(s: string): string {
	return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}
